import { EmailButton, EmailSendModal, SharedTable } from '@components';
import { useCustomerEstimateReminderInvite, useDemoGuard, useTenantVat } from '@hooks';
import { L } from '@lib';
import { IOrderApprovalHistoryHook, OrderStatus } from '@models';
import {
  CreateServiceOrder,
  NotificationService,
  OrderApprovalHistory as OrderApprovalHistoryType,
  OrderService,
} from '@services';
import { useStore } from '@stores';
import { Button, Col, Modal, Popconfirm, Row, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import EstimatePrintContent from './EstimatePrintContent';
import { Columns, ExpandedRowRender, resolveStatus } from './OrderApprovalHistory.columns';
import ReviseEstimateDrawer from './ReviseEstimateDrawer';

const APPROVAL_RESPONSE_TYPES = ['Phone', 'Sms', 'Email', 'InPerson', 'Chat'];

interface IProps {
  approvalHistoryHook: IOrderApprovalHistoryHook;
  customerEmail?: string;
  customerId?: string;
  customerUserId?: number;
  customerFullName?: string;
  orderStatus?: string;
  orderId?: string;
  registrationNumber?: string;
}

const OrderApprovalHistory: React.FC<IProps> = ({
  approvalHistoryHook,
  customerEmail,
  customerId,
  customerUserId,
  customerFullName,
  orderStatus,
  orderId,
  registrationNumber,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [approveModal, setApproveModal] = useState<{
    visible: boolean;
    record?: OrderApprovalHistoryType;
  }>({
    visible: false,
  });
  const [reviseDrawer, setReviseModal] = useState<{
    visible: boolean;
    record?: OrderApprovalHistoryType;
  }>({
    visible: false,
  });
  const [revisingId, setRevisingId] = useState<string | null>(null);

  const [selectedType, setSelectedType] = useState<string | undefined>();
  const [emailModal, setEmailModal] = useState<{ visible: boolean; orderId?: string }>({
    visible: false,
  });
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const { orderStore, customerStore, sessionStore, serviceStore } = useStore();
  const tenantVat = useTenantVat();
  const { guard } = useDemoGuard();

  // App reminder
  const { customerInvite, loading: customerInviteLoading } =
    useCustomerEstimateReminderInvite(customerId);
  const isReminderDisabled =
    !!customerInvite || !customerUserId || orderStatus !== OrderStatus.Approval;

  const handleReminderSend = useCallback(async () => {
    if (!customerId) return;
    await NotificationService.SendCustomerEstimateInvitation({
      id: customerId,
      orderId: orderId || '',
    }).then(() => {
      customerStore.setCustomerEstimateReminderInvite({
        id: customerId,
        type: 'EstimateReminder',
        customerId,
        creationDate: dayjs().format(),
      });
    });
  }, [customerId, orderId, customerStore]);

  const toggleExpand = (id: string) => {
    setExpandedKeys((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]));
  };

  const handleApprove = async () => {
    if (!approveModal.record || !selectedType) return;
    const result = await orderStore.updateApprovalHistory({
      id: approveModal.record.id,
      isApproved: true,
      approvalResponseType: selectedType,
    });
    // Backend moves the order to "Estimate - Approved" when an estimate awaiting
    // approval is approved; mirror it locally so the UI updates without a refetch.
    if (result && orderId && orderStore.order?.status === OrderStatus.Approval) {
      orderStore.updateOrdersStatus(orderId, OrderStatus.Approved);
    }
    setApproveModal({ visible: false });
    setSelectedType(undefined);
  };

  const handleReviseSubmit = async (serviceOrders: CreateServiceOrder[]) => {
    if (!reviseDrawer.record) return;
    try {
      await orderStore.reviseApproval({
        id: reviseDrawer.record.id,
        serviceOrders,
      });
      setReviseModal({ visible: false });
      // Re-hydrate the Job details tab from the revised order so it updates
      // without a manual refresh (initFromOrder has an early-exit guard, so we
      // reload the canonical order and force a clean re-init).
      const effectiveOrderId = reviseDrawer.record?.orderId ?? orderId;
      if (effectiveOrderId) {
        await orderStore.get({ id: effectiveOrderId });
        // Only re-sync the service editor when inside the order edit view
        if (orderId && orderStore.order) {
          serviceStore.clearOrderServices();
          serviceStore.initFromOrder(orderStore.order, tenantVat);
        }
      }
    } catch {
      // keep drawer open so user doesn't lose edits
    }
  };

  const validateEmail = (val: string) => {
    if (!val) return 'Required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) return 'Invalid email address';
    return null;
  };

  const handleEmailOpen = guard((orderId: string) => {
    const initial = customerEmail?.trim() || '';
    setEmail(initial);
    setEmailError(validateEmail(initial));
    setEmailModal({ visible: true, orderId });
  });

  const handleEmailCancel = () => {
    setEmailModal({ visible: false });
    setEmail('');
    setEmailError(null);
  };

  const handleEmailChange = (val: string) => {
    setEmail(val);
    setEmailError(validateEmail(val));
  };

  const handleEmailSend = async () => {
    if (!emailModal.orderId) return;
    const error = validateEmail(email);
    setEmailError(error);
    if (error) return;

    setEmailSending(true);
    try {
      await OrderService.sendEstimateEmail(emailModal.orderId, email || undefined);
      setEmailModal({ visible: false });
      setEmail('');
    } catch {
      // error handled by service
    } finally {
      setEmailSending(false);
    }
  };

  const [printRecord, setPrintRecord] = useState<OrderApprovalHistoryType | null>(null);
  const printContentRef = useRef<HTMLDivElement>(null);
  const handleReactPrint = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: 'Estimate',
    onAfterPrint: () => setPrintRecord(null),
  });

  const handlePrintClick = (record: OrderApprovalHistoryType) => {
    flushSync(() => setPrintRecord(record));
    handleReactPrint();
  };

  const tenantName = sessionStore.currentLogin?.tenant?.name;

  const columns = [
    ...Columns(),
    {
      title: '',
      key: 'actions',
      width: 560,
      render: (_: any, record: OrderApprovalHistoryType) => {
        const status = resolveStatus(record);
        // Revise supersedes the estimate and creates a new record. Allow it for
        // approved estimates too, so a mistakenly-approved estimate can be revised.
        // record.orderId is always present; the order is loaded lazily on click.
        const canRevise = (status === 'Pending' || status === 'Approved') && !!record.orderId;
        return (
        <Row justify="end" gutter={8} wrap={false} style={{ flexWrap: 'nowrap' }}>
          {status === 'Pending' && (
            <Col>
              <Button
                size="small"
                type="primary"
                onClick={() => setApproveModal({ visible: true, record })}
              >
                {L('Approve')}
              </Button>
            </Col>
          )}
          {canRevise && (
            <Col>
              <Button
                size="small"
                loading={revisingId === record.id}
                disabled={revisingId !== null && revisingId !== record.id}
                onClick={async () => {
                  setRevisingId(record.id);
                  try {
                    if (orderStore.order?.id !== record.orderId) {
                      await orderStore.get({ id: record.orderId });
                    }
                    setReviseModal({ visible: true, record });
                  } finally {
                    setRevisingId(null);
                  }
                }}
              >
                {L('Revise')}
              </Button>
            </Col>
          )}
          {customerId && (
            <Col>
              <Popconfirm
                title={`Send app reminder to ${customerFullName || 'customer'}?`}
                onConfirm={handleReminderSend}
                okText="Yes, send"
                cancelText="Cancel"
                okButtonProps={{ loading: customerInviteLoading }}
                disabled={isReminderDisabled}
              >
                <Button size="small" loading={customerInviteLoading} disabled={isReminderDisabled}>
                  {customerInvite ? L('Already Reminded Today') : L('Send app reminder')}
                </Button>
              </Popconfirm>
            </Col>
          )}
          {record.snapshot && (
            <Col>
              <Button
                size="small"
                disabled={!!printRecord}
                onClick={() => handlePrintClick(record)}
              >
                {L('Print PDF')}
              </Button>
            </Col>
          )}
          <Col>
            <EmailButton size="small" onClick={() => handleEmailOpen(record.orderId)} />
          </Col>
          {record.snapshot && (
            <Col>
              <Button size="small" onClick={() => toggleExpand(record.id)}>
                {expandedKeys.includes(record.id) ? L('Hide details') : L('Show details')}
              </Button>
            </Col>
          )}
        </Row>
        );
      },
    },
  ];

  return (
    <>
      <SharedTable
        columns={columns}
        dataSource={approvalHistoryHook.dataSource}
        loading={approvalHistoryHook.loading}
        emptyText={L('No estimates')}
        maxResultCount={approvalHistoryHook.maxResultCount}
        currentPage={approvalHistoryHook.currentPage}
        totalCount={approvalHistoryHook.totalCount}
        handlePageChange={approvalHistoryHook.handlePageChange}
        handleSortChange={approvalHistoryHook.handleSortChange}
        expandable={{
          expandedRowKeys: expandedKeys,
          expandedRowRender: (record: OrderApprovalHistoryType) => ExpandedRowRender(record),
          rowExpandable: (record: OrderApprovalHistoryType) => !!record.snapshot,
          showExpandColumn: false,
        }}
      />
      <Modal
        title={L('Approve Estimate')}
        open={approveModal.visible}
        onOk={handleApprove}
        okButtonProps={{ disabled: !selectedType }}
        onCancel={() => {
          setApproveModal({ visible: false });
          setSelectedType(undefined);
        }}
        okText={L('Approve')}
      >
        <p>{L('How was this estimate approved?')}</p>
        <Select
          style={{ width: '100%' }}
          placeholder={L('Select approval method')}
          value={selectedType}
          onChange={setSelectedType}
          options={APPROVAL_RESPONSE_TYPES.map((t) => ({
            label: t === 'InPerson' ? 'In Person' : t,
            value: t,
          }))}
        />
      </Modal>
      <EmailSendModal
        open={emailModal.visible}
        title={L('Send estimate email')}
        emailValue={email}
        setEmailValue={handleEmailChange}
        emailError={emailError}
        onConfirm={handleEmailSend}
        onCancel={handleEmailCancel}
        confirmText={L('Send')}
        okButtonProps={{ disabled: !!emailError, loading: emailSending }}
      />
      <ReviseEstimateDrawer
        open={reviseDrawer.visible}
        order={orderStore.order}
        onCancel={() => setReviseModal({ visible: false })}
        onSubmit={handleReviseSubmit}
      />
      <div style={{ position: 'fixed', left: -10000, top: 0 }}>
        <EstimatePrintContent
          ref={printContentRef}
          record={printRecord ?? undefined}
          tenantName={tenantName}
          customer={orderStore.order?.customer}
          customerFullName={customerFullName}
          registrationNumber={registrationNumber}
          vehicleModel={orderStore.order?.vehicleModel}
          mileage={orderStore.order?.mileage}
        />
      </div>
    </>
  );
};

export default OrderApprovalHistory;
